import WixProductOnboarding from './WixProductOnboarding';
import { resolveWixFlow } from './agent-creation/wixFlow';
import WixProductAccount from './WixProductAccount';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import WixEntryLoader from '../components/WixEntryLoader';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { wixHandshake, wixLocal, wixMultiEnabled, wixRequest, setWixGrant } from '../utils/wixProductApi';
import './WixProductEntry.css';
import './WixOnboarding.css';
import { acceptWixEntrySession } from '../utils/wixEntrySession';
import { finishWixConnect, validWixInstaller } from '../utils/wixConnect';
import { selectBotFromDeepLink } from '../utils/deepLinks';
import { getCurrentOrgId, setCurrentOrgId, setRole, clearWorkspaceScopedSelections } from '../utils/permissions';
import { instagramOAuthResult } from '../utils/instagramOAuth';
import InstagramOAuthReturn from './InstagramOAuthReturn';
import { whatsappOAuthResult } from '../utils/whatsappOAuth';
import WhatsAppOAuthReturn from './WhatsAppOAuthReturn';

export default function WixProductEntry() {
  const location = useLocation();
  const [search] = useState(() => window.__UB_WIX_CALLBACK__?.path === window.location.pathname
    ? window.__UB_WIX_CALLBACK__.search : location.search);
  const [instagramResult] = useState(() => instagramOAuthResult(search));
  const [whatsappResult] = useState(() => whatsappOAuthResult(search));
  if (instagramResult) return <InstagramOAuthReturn result={instagramResult} />;
  return whatsappResult ? <WhatsAppOAuthReturn result={whatsappResult} /> : <WixProductSetup />;
}

function WixProductSetup() {
  const { t } = useTranslation();
  const { appKey } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [disconnected, setDisconnected] = useState(false);
  const linking = useRef(null);
  const opening = useRef(null);
  const openingInFlight = useRef(false);
  const entrySearch = useRef(window.__UB_WIX_CALLBACK__?.path === window.location.pathname
    ? window.__UB_WIX_CALLBACK__.search : location.search);
  const flow = resolveWixFlow(state);
  const fullFlow = !!flow;

  const reload = async () => {
    const next = await wixRequest(appKey, 'state');
    setState(next);
    setName(next.draft?.name || '');
    return next;
  };

  useEffect(() => {
    let live = true;
    setState(null); setError(''); setDisconnected(false);
    if (!wixMultiEnabled) { setError('This app is not available.'); return undefined; }
    (async () => {
      const handshake = await wixHandshake(appKey, entrySearch.current || location.search);
      if (!live) return;
      if (handshake?.action === 'connected') {
        delete window.__UB_WIX_CALLBACK__;
        finishWixConnect(handshake);
        return;
      }
      if (handshake?.url) {
        if (wixLocal) throw new Error('Real Wix installation is disabled in this local preview. Use the test app links.');
        window.location.assign(validWixInstaller(handshake.url));
        return;
      }
      if (handshake?.grant) {
        acceptWixEntrySession(handshake);
        setWixGrant(appKey, handshake.grant);
        entrySearch.current = '';
        delete window.__UB_WIX_CALLBACK__;
        // Remove authorization material before loading the rest of the page.
        navigate(`/wix/${appKey}/start`, { replace: true });
      }
      const data = await wixRequest(appKey, 'state');
      if (live) { setState(data); setName(data.draft?.name || ''); }
    })().catch(e => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [appKey, location.search, navigate, retry]);

  useEffect(() => {
    const changed = e => {
      if (e.key === 'token' || e.key === null) {
        setState(null);
        reload().catch(err => setError(err.message));
      }
    };
    window.addEventListener('storage', changed);
    return () => window.removeEventListener('storage', changed);
    // appKey is the scope; do not rerun on every response.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey]);

  // Create the installation's agent directly after authentication, just as the
  // original Wix flow does. No second account or generic agent-name landing.
  useEffect(() => {
    if (!fullFlow || state.action !== 'ONBOARDING' || state.bot_id || error) return;
    if (!linking.current || linking.current.appKey !== appKey) linking.current = { appKey, promise: wixRequest(appKey, 'link', {
      method: 'POST', body: JSON.stringify({ name: state.draft?.name || flow.default_agent_name }),
    }) };
    let active = true;
    linking.current.promise.then(async () => {
      if (!active) return;
      linking.current = null;
      await reload();
    }).catch(e => {
      if (active) { linking.current = null; setError(e.message); }
    });
    return () => { active = false; };
    // Installation identity is fixed for this entry, and the request is coalesced.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appKey, state, error, fullFlow]);

  async function perform(fn) {
    setBusy(true); setError('');
    try { await fn(); await reload(); } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function localLogin(user) {
    const response = await fetch(`http://127.0.0.1:58139/api/local/login/${user}`, { method: 'POST' });
    if (!response.ok) throw new Error('Local sign-in failed.');
    sessionStorage.setItem('wix-local-session', (await response.json()).token);
  }

  async function disconnect() {
    setBusy(true); setError('');
    try {
      await wixRequest(appKey, 'connection', { method: 'DELETE' });
      setWixGrant(appKey, '');
      setState(null); setDisconnected(true);
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  const openAgent = useCallback(async () => {
    if (openingInFlight.current) return;
    openingInFlight.current = true;
    setBusy(true); setError('');
    try {
      // Recheck immediately before navigation, including Save/exit and Finish.
      // A deletion in another tab must restart setup, never select a stale ID.
      const current = await wixRequest(appKey, 'state');
      if (current.action === 'LOGIN' || !current.bot_id) {
        linking.current = null;
        opening.current = null;
        openingInFlight.current = false;
        clearWorkspaceScopedSelections();
        setState(current); setBusy(false);
        return;
      }
      if (current.organization_id && getCurrentOrgId() !== current.organization_id) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}orgs/switch`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: JSON.stringify({ organization_id: current.organization_id }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.detail || 'Could not open this workspace.');
        localStorage.setItem('token', data.token);
        setRole(data.role, data.owner_user_id);
        setCurrentOrgId(data.current_org_id);
        clearWorkspaceScopedSelections();
      }
      selectBotFromDeepLink(current.bot_id);
      // Stay in the Wix dashboard's iframe. A document reload also lets the
      // portal bootstrap the verified session just saved by this entry flow.
      window.location.replace(`/overview?bot_id=${encodeURIComponent(current.bot_id)}`);
    } catch (e) { openingInFlight.current = false; setError(e.message); setBusy(false); return { error: e.message }; }
  }, [appKey]);

  useEffect(() => {
    if (wixLocal || state?.action !== 'DASHBOARD' || !state.bot_id || !state.completed || error) return;
    const destination = `${appKey}:${state.installation_id}:${state.bot_id}`;
    if (opening.current === destination) return;
    opening.current = destination;
    openAgent();
  }, [appKey, fullFlow, state, error, openAgent]);

  if (!wixLocal && state?.action === 'DASHBOARD' && state.bot_id && state.completed && !error) return <WixEntryLoader />;
  if (state?.action === 'LOGIN' && !wixLocal) return <main className="wxo-scope wxo-wrapper">
    <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
    <div className="wxo-stage" style={{ maxWidth: 980 }}><div className="wxo-card-anim">
      <WixProductAccount onAuthenticated={reload} />
    </div></div>
  </main>;
  if (fullFlow && !state.bot_id && !error) return <WixEntryLoader />;
  if (fullFlow && state?.bot_id && !state.completed) {
    return <WixProductOnboarding appKey={appKey} state={state} reload={reload} openAgent={openAgent} />;
  }
  const knownFlow = fullFlow || (wixLocal && state?.onboarding?.mode === 'foundation');
  if (!state && !error && !disconnected) return <WixEntryLoader />;
  return <main className="wxo-scope wxo-wrapper wix-product-entry">
    <img className="wxo-logo" src="/ultimo-bots-white.png" alt="Ultimo Bots" />
    <div className="wxo-stage"><section className="wxo-card wix-product-card">
    <p className="wix-product-eyebrow">{t('wix_entry_setup')}</p>
    <h1 className="wxo-h2">{state?.onboarding?.title || t('wix_entry_title')}</h1>
    {error && <div role="alert" className="wix-product-error"><strong>{t('wix_entry_error_title')}</strong><p>{error}</p></div>}
    {error && (!wixLocal || !state || fullFlow) && <><p className="wxo-sub">{t('wix_entry_error_help')}</p>
      <button className="wxo-btn" onClick={() => { opening.current = null; setRetry(value => value + 1); }}>{t('wix_entry_retry')}</button></>}
    {disconnected && <p role="status">This app is disconnected. Reopen it from Wix to reconnect.</p>}
    {!state && !error && !disconnected && <p role="status">Checking your installation…</p>}
    {state?.action === 'LOGIN' && <>
      <p>Sign in or create an Ultimo Bots account to continue setting up this app.</p>
      {wixLocal ? <div className="wix-product-actions">
        <button className="wxo-btn" disabled={busy} onClick={() => perform(() => localLogin(1))}>Use test account 1</button>
        <button className="wxo-btn" disabled={busy} onClick={() => perform(() => localLogin(2))}>Use test account 2</button>
      </div> : <div className="wix-product-actions">
        <Link className="wxo-btn-ghost" to={`/login?returnTo=${encodeURIComponent(`/wix/${appKey}/start`)}`}>Sign in</Link>
        <Link className="wxo-btn" to={`/register?returnTo=${encodeURIComponent(`/wix/${appKey}/start`)}`}>Create account</Link>
      </div>}
    </>}
    {state && state.action !== 'LOGIN' && !knownFlow && <p>This onboarding experience is not available yet.</p>}
    {state && state.action !== 'LOGIN' && knownFlow && !fullFlow && <>
      {state.completed ? <>
        <h2>Your app setup is saved</h2>
        <p>This installation has its own agent and onboarding progress.</p>
        {!wixLocal && <button className="wxo-btn" disabled={busy} onClick={openAgent}>Open your agent</button>}
      </> : <>
        <p>{fullFlow ? 'Create your agent to start setup.' : `Step ${state.step + 1} of 3 · Set up an independent agent for this app.`}</p>
        <label htmlFor="wix-agent-name">Agent name</label>
        <input id="wix-agent-name" maxLength={120} value={name} readOnly={!!state.bot_id} onChange={e => setName(e.target.value)} />
        {!state.bot_id ? <button className="wxo-btn" disabled={busy || !name.trim()} onClick={() => perform(() => wixRequest(appKey, 'link', {
          method: 'POST', body: JSON.stringify({ name }),
        }))}>Create agent</button> : <div className="wix-product-actions">
          <button className="wxo-btn" disabled={busy} onClick={() => perform(() => wixRequest(appKey, 'progress', {
            method: 'PUT', body: JSON.stringify({ step: Math.min(state.step + 1, 2), revision: state.revision, draft: { name }, complete: state.step >= 2 }),
          }))}>{state.step >= 2 ? 'Finish setup' : 'Save and continue'}</button>
          <button className="wxo-btn" disabled={busy} onClick={() => perform(() => wixRequest(appKey, 'progress', {
            method: 'PUT', body: JSON.stringify({ step: state.step, revision: state.revision, draft: { name } }),
          }))}>Save for later</button>
        </div>}
        <p className="wix-product-note">{fullFlow ? 'Your setup continues with channels, knowledge, design and a live agent test.' : 'This foundation flow verifies app setup.'}</p>
      </>}
      <button className="wxo-btn-ghost wix-product-secondary" disabled={busy} onClick={disconnect}>Disconnect this app</button>
    </>}
    {wixLocal && <Link className="wix-product-home" to="/">Back to test apps</Link>}
  </section></div></main>;
}
